import { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import { logger } from "@/lib/logger";
import { toast } from "sonner";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = auth
  const kickedRef = useRef(false); // guards against duplicate session-kick toasts

  const checkSession = useCallback(async () => {
    try {
      const me = await api.get("/auth/me");
      setUser(me);
    } catch (e) {
      logger.debug("No active session:", e?.status || e);
      setUser(false);
    }
  }, [setUser]);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  // Unload Guard: warn before leaving/closing the tab while a session is active.
  useEffect(() => {
    if (!user) return; // only when authenticated (user is an object; null=checking, false=anon)
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "You have an active session. Are you sure you want to leave?";
      return e.returnValue;
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [user]);

  const login = useCallback(async (username, password) => {
    const { getDeviceId } = await import("@/lib/device");
    const u = await api.post("/auth/login", { username, password, device_id: getDeviceId() });
    kickedRef.current = false; // allow a fresh kick to notify in this tab's lifetime
    setUser(u);
    return u;
  }, [setUser]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      logger.error("Logout request failed (clearing session anyway):", e);
    }
    setUser(false);
  }, [setUser]);

  const value = useMemo(() => ({ user, setUser, login, logout }), [user, login, logout]);

  // Single-active-session: any API call that returns 401 session_conflict dispatches this event.
  useEffect(() => {
    const onKick = (e) => {
      if (kickedRef.current) return; // one toast even if several calls 401 at once
      kickedRef.current = true;
      toast.error(e.detail?.message || "You have been logged out because this account is active on another device.", { duration: 12000 });
      logout();
    };
    window.addEventListener("proplan:session-kick", onKick);
    return () => window.removeEventListener("proplan:session-kick", onKick);
  }, [logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
